/**
 * ScreenAnswersPanel — the lead's screening picture, inline (owner ask
 * 2026-08-04: the Review queue "does not let us review the form", so
 * recruiters had to trek to the lead page and lose their place).
 *
 * Renders the server-computed `screen` payload — every answered question with
 * its verdict dot, answer, provenance chip (questionnaire / web form / earlier
 * screen) and the lead's verbatim quote. The same truth AgentPanel's "Screen
 * details" shows, now where the determination happens.
 *
 * Presentational: `useConversationScreen` does the fetching, so the caller can
 * feed ONE read to both this panel and the flag breakdowns above it (whose
 * stored payloads may predate the answer-label fix).
 */
import { useEffect, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { prettySlug } from '../../lib';

export interface ScreenItem {
  question_key: string;
  question: string | null;
  answered: boolean;
  declined: boolean;
  value: unknown;
  /** The lead's answer in WORDS when the stored value is an option key
   *  ("option_4" → "Heart attack"); absent when the raw value reads as itself. */
  value_label?: string | null;
  verdict: 'pass' | 'fail' | 'unknown' | null;
  quote: string | null;
  source?: string | null;
}

export interface ScreenPayload {
  session_id: string;
  total: number;
  answered: number;
  complete: boolean;
  items?: ScreenItem[];
}

/**
 * One conversation-detail read, shared. Returns the screen block plus a
 * question_key → answer-wording map the caller hands to EscalationBreakdown so
 * flags stored before the fix still read as words.
 */
export function useConversationScreen(conversationId: string) {
  const [screen, setScreen] = useState<ScreenPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setScreen(null);
    api.get<{ screen?: ScreenPayload | null }>(`/api/crm/agent/conversations/${conversationId}`)
      .then((res) => { if (alive) setScreen(res.data?.screen || null); })
      .catch(() => { /* fail-soft: the panel simply doesn't render rows */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [conversationId]);

  const answerLabels = new Map<string, string>();
  for (const it of screen?.items || []) {
    if (it.value_label) answerLabels.set(it.question_key, it.value_label);
  }
  return { screen, loading, answerLabels };
}

// The same verdict palette as AgentPanel / the prescreen surfaces.
const VERDICT_DOT: Record<string, string> = {
  pass: 'bg-green-500',
  fail: 'bg-red-500',
  unknown: 'bg-amber-400 dark:bg-amber-500',
  none: 'bg-gray-300 dark:bg-gray-600',
};

const VERDICT_SR: Record<string, string> = {
  pass: 'passed:', fail: 'failed:', unknown: 'needs review:', none: 'recorded:',
};

const SOURCE_CHIP: Record<string, string> = {
  questionnaire: 'questionnaire',
  landing: 'web form',
  prescreen: 'earlier screen',
  earlier_chat: 'earlier text chat',
  derived: 'calculated',
};

const questionLabel = (key: string): string => prettySlug(key.split('.').pop() || key);

const answerLabel = (value: unknown): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined) return 'Declined to answer';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

export default function ScreenAnswersPanel({ screen, loading }: {
  screen: ScreenPayload | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading screening answers…
      </div>
    );
  }
  const items = (screen?.items || []).filter((it) => it.answered);
  if (!items.length) {
    return (
      <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
        No screening answers captured yet — open the thread for the conversation so far.
      </p>
    );
  }

  const judged = items.filter((it) => it.verdict);
  const passing = judged.filter((it) => it.verdict === 'pass').length;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-gray-100">
        <ClipboardList className="h-3.5 w-3.5 text-gray-400" />
        Screening answers
        <span className="font-normal text-gray-400 dark:text-gray-500">
          · {screen?.answered ?? items.length}/{screen?.total ?? items.length} answered
          {judged.length > 0 && ` · ${passing}/${judged.length} passing`}
        </span>
      </div>
      <ul className="mt-1.5 max-h-72 overflow-y-auto pr-1">
        {items.map((it) => {
          const verdict = it.verdict || (it.declined ? 'unknown' : 'none');
          const source = it.source && SOURCE_CHIP[it.source] ? SOURCE_CHIP[it.source] : null;
          return (
            <li key={it.question_key} className="flex items-start gap-2 py-1">
              <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${VERDICT_DOT[verdict]}`} aria-hidden="true" />
              <span className="sr-only">{VERDICT_SR[verdict]}</span>
              <div className="min-w-0">
                <p className="text-xs text-gray-700 dark:text-gray-200" title={it.question || undefined}>
                  {/* The real question, not the form's auto-generated key. */}
                  {it.question || questionLabel(it.question_key)}
                  {source && (
                    <span
                      title={it.source === 'derived' ? 'Calculated from their answers' : 'Pre-filled from what the lead already told us'}
                      className="ml-1 rounded-full bg-sky-100 px-1.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                    >
                      {source}
                    </span>
                  )}
                  {/* The lead's WORDS win over the stored option value. */}
                  <span className="text-gray-400 dark:text-gray-500">{' '}· </span>
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {it.declined ? 'Declined to answer' : (it.value_label || answerLabel(it.value))}
                  </span>
                </p>
                {it.quote && (
                  <p className="mt-0.5 truncate text-[11px] italic text-gray-400 dark:text-gray-500" title={it.quote}>
                    “{it.quote}”
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
